import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { SiteDocProjectChoice, SiteDocProjectContact } from '@symphenia/shared-types'
import { useTerminology } from '@/lib/useTerminology'
import { cn } from '@/lib/utils'
import { fetchProjectChoices, type TagebuchBezug } from '../api/site-docs.api'

/** Das gewählte Projekt: aus dem Projektstamm, aus der Zeiterfassung oder frei eingetragen (AP-46 24.). */
export interface ProjektWahlWert {
  bezug: TagebuchBezug
  label: string
  kontakte: SiteDocProjectContact[]
}

export const wahlAus = (p: SiteDocProjectChoice): ProjektWahlWert => ({
  bezug: p.source === 'PROJECT' ? { projectId: p.id } : { projectRefId: p.id },
  label: [p.number, p.name].filter(Boolean).join(' '),
  kontakte: p.contacts,
})

/**
 * Projektwahl der Baustellendokumente (`GET /site-docs/projects`): Suche über die Auswahl des Benutzers,
 * daneben ein frei eingetragenes Projekt über seinen Text — so stehen die Berichte dort auch in der Akte.
 */
export function ProjektWahl({ wert, onWahl }: { wert: ProjektWahlWert | null; onWahl: (w: ProjektWahlWert | null) => void }) {
  const { t } = useTranslation()
  const terms = useTerminology()
  const [suche, setSuche] = useState('')
  const [liste, setListe] = useState<SiteDocProjectChoice[]>([])
  const [frei, setFrei] = useState(false)
  const [freiText, setFreiText] = useState('')
  const [offen, setOffen] = useState(!wert)

  useEffect(() => {
    const z = setTimeout(() => fetchProjectChoices(suche.trim() || undefined).then((r) => setListe(r.data)).catch(() => setListe([])), suche ? 300 : 0)
    return () => clearTimeout(z)
  }, [suche])

  const feld = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm'

  if (wert && !offen) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">{terms.project}:</span>
        <span className="font-medium">{wert.label}</span>
        <button type="button" className="text-primary hover:underline" onClick={() => setOffen(true)}>
          {t('siteDocs.diary.changeProject')}
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-xl space-y-2 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('siteDocs.diary.chooseProject', { project: terms.project })}</p>
      {frei ? (
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (!freiText.trim()) return
            onWahl({ bezug: { projectLabel: freiText.trim() }, label: freiText.trim(), kontakte: [] })
            setOffen(false)
          }}
        >
          <input className={feld} aria-label={t('siteDocs.diary.freeProject', { project: terms.project })} placeholder={t('siteDocs.diary.freeProject', { project: terms.project })} value={freiText} onChange={(e) => setFreiText(e.target.value)} />
          <button type="submit" disabled={!freiText.trim()} className="h-9 rounded-md border px-3 disabled:opacity-50">
            {t('siteDocs.diary.show')}
          </button>
        </form>
      ) : (
        <>
          <input className={feld} aria-label={t('siteDocs.create.search')} placeholder={t('siteDocs.create.search')} value={suche} onChange={(e) => setSuche(e.target.value)} />
          <ul className="max-h-56 space-y-1 overflow-y-auto">
            {liste.map((p) => (
              <li key={`${p.source}-${p.id}`}>
                <button
                  type="button"
                  onClick={() => {
                    onWahl(wahlAus(p))
                    setOffen(false)
                  }}
                  className={cn('w-full rounded-md border px-3 py-1.5 text-left', (wert?.bezug.projectId ?? wert?.bezug.projectRefId) === p.id && 'border-primary bg-primary/10')}
                >
                  <span className="font-medium">{[p.number, p.name].filter(Boolean).join(' ')}</span>
                  {p.customerName && <span className="ml-2 text-xs text-muted-foreground">{p.customerName}</span>}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="flex gap-3">
        <button type="button" onClick={() => setFrei((f) => !f)} className="text-primary hover:underline">
          {frei ? t('siteDocs.create.fromList') : t('siteDocs.create.free')}
        </button>
        {wert && (
          <button type="button" onClick={() => setOffen(false)} className="text-muted-foreground hover:underline">
            {t('common.cancel')}
          </button>
        )}
      </div>
    </div>
  )
}
